// Dependencies
import { ReactNode } from 'react';
import { m } from 'framer-motion';

// Material UI Components
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// Internal Components
import Logo from '../components/Logo';
import { MotionContainer, varFade } from 'src/components/animate';
import LanguagePopover from './dashboard/navbar/LanguagePopover';
import ModePopover from './dashboard/navbar/ModePopover';
import { IntercomChat } from 'src/components/IntercomChat';

// Components
const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  left: 0,
  lineHeight: 0,
  width: '100%',
  position: 'absolute',
  padding: theme.spacing(3, 3, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 5, 0),
  },
}));

// Types
type Props = {
  useBackground?: boolean;
  useLanguage?: boolean;
  useTheme?: boolean;
  children?: ReactNode;
};

/**
 * Logo Only Layout Component
 */
export default function LogoOnlyLayout({ children, useBackground, useLanguage, useTheme }: Props) {
  // =={ Renders }===============================================================
  return (
    <Box
      component={MotionContainer}
      sx={
        useBackground
          ? {
              '&:before': {
                width: '100%',
                height: '100%',
                zIndex: 1,
                content: '""',
                opacity: (theme) => (theme.palette.mode === 'light' ? 0.24 : 0.08),
                position: 'fixed',
                transform: 'translate3d(0, 0, 0)',
                overflow: 'hidden',
                perspective: 1000,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundImage: 'url("/assets/background-3-blur.webp")',
              },
            }
          : undefined
      }
    >
      <HeaderStyle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: 'relative',
            zIndex: 99,
          }}
          gap={1}
        >
          <m.div variants={varFade().inDown}>
            <Logo />
          </m.div>
          <Box flexGrow={1} />
          {useLanguage && <LanguagePopover />}
          {useTheme && <ModePopover sideBarOpen={true} />}
        </Stack>
      </HeaderStyle>
      <IntercomChat />

      {children}
    </Box>
  );
}
